import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth, isPreviewAuthBypassActive } from "@/lib/firebase";

const PREVIEW_USER = {
  uid: "preview-bypass-user",
  email: "preview@swordpoint.local",
  displayName: "Preview User",
  isPreviewBypass: true,
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const bypass = isPreviewAuthBypassActive();
  const [user, setUser] = useState(bypass ? PREVIEW_USER : null);
  const [loading, setLoading] = useState(!bypass);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (bypass) return;
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setAuthError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (e) {
      setAuthError(e.message || "Sign in failed.");
      return false;
    }
  };

  const register = async (email, password) => {
    setAuthError("");
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      return true;
    } catch (e) {
      setAuthError(e.message || "Registration failed.");
      return false;
    }
  };

  const logout = async () => {
    if (bypass) return;
    await signOut(auth);
  };

  const value = useMemo(
    () => ({ user, loading, authError, login, register, logout, isPreviewBypass: bypass }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, authError, bypass]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
